/**
 * MCQ — web port of iOS
 * `client-native/.../components/PersonalizedMCQ.tsx`.
 *
 * Handles BOTH forward and reverse MCQ via `isReverse`:
 *   - forward: title "What does this mean?", question = Spanish, options = English
 *   - reverse: title "How do you say this?", question = English, options = Spanish
 * (The generator already shapes question/options correctly per type; this
 * component only flips the title and the tracked activity name.)
 *
 * Retry-until-correct (§4): wrong tap → red flash + shake → reset to default
 * after 500ms → keep trying. The correct answer is NEVER revealed; there is NO
 * "Continue" button on wrong. Correct tap → green flash →
 *   - feed: auto onFinish() immediately (iOS PersonalizedMCQ:100-101).
 *   - lesson: 600ms → CorrectDrawer (confirm-to-continue), advance on Continue
 *     via onFinish(false) (iOS PersonalizedMCQ:103 + CorrectDrawer 184-195).
 */
import { useMemo, useRef, useState } from "react";
import type { GuessActivity } from "@shared_types";
import ActivityButton, {
  type ActivityButtonRef,
  type ActivityButtonState,
} from "../ui/ActivityButton";
import ParrotBubble from "../ui/ParrotBubble";
import CorrectDrawer from "../ui/CorrectDrawer";
import parrotSitting from "@/assets/parrot-sitting.png";
import { colors } from "@/ui";
import type { ActivityComponentContract } from "../types";
import {
  activityContainerStyle,
  buttonsGridStyle,
  gridCellStyle,
  titleStyle,
} from "./layout";

const WRONG_RESET_MS = 500;
/** Lesson: hold the green flash before sliding up the CorrectDrawer (iOS 600ms). */
const LESSON_CORRECT_DRAWER_DELAY_MS = 600;

export interface MCQProps extends ActivityComponentContract {
  data: GuessActivity;
  /** Reverse MCQ (English prompt → Spanish answer). */
  isReverse?: boolean;
}

export default function MCQ({
  data,
  isReverse = false,
  variant = "lesson",
  onFinish,
  onTrackAttempt,
}: MCQProps) {
  const isFeed = variant === "feed";
  const [clickedIndex, setClickedIndex] = useState<number | null>(null);
  const [buttonStates, setButtonStates] = useState<
    Record<number, ActivityButtonState>
  >({});
  const [showCorrectDrawer, setShowCorrectDrawer] = useState(false);
  const buttonRefs = useRef<Record<number, ActivityButtonRef | null>>({});
  // Per-activity attempt counter (1,2,3…) → attempt_number / is_first_attempt.
  const attemptRef = useRef(0);

  const trackName = isReverse ? "mcq-reverse" : "mcq";

  const options = useMemo(() => data?.options ?? [], [data]);
  const correctChoice = useMemo(
    () => options.find((o) => o.correct)?.choice,
    [options],
  );

  const handleClick = (correct: boolean, index: number) => {
    if (clickedIndex !== null) return;
    setClickedIndex(index);

    if (correct) {
      onTrackAttempt?.(trackName, true, ++attemptRef.current);
      buttonRefs.current[index]?.triggerCorrect();
      setButtonStates((p) => ({ ...p, [index]: "matched" }));
      if (isFeed) {
        // Feed: auto-advance immediately (iOS PersonalizedMCQ feed onFinish()).
        onFinish();
      } else {
        // Lesson: hold the green flash, then slide up the CorrectDrawer.
        window.setTimeout(
          () => setShowCorrectDrawer(true),
          LESSON_CORRECT_DRAWER_DELAY_MS,
        );
      }
      return;
    }

    onTrackAttempt?.(trackName, false, ++attemptRef.current);
    buttonRefs.current[index]?.triggerIncorrect();
    setButtonStates((p) => ({ ...p, [index]: "incorrect" }));
    window.setTimeout(() => {
      setButtonStates((p) => ({ ...p, [index]: "default" }));
      setClickedIndex(null);
    }, WRONG_RESET_MS);
  };

  if (!data) {
    return (
      <div style={activityContainerStyle(isFeed)}>
        <span style={{ color: isFeed ? colors.white : colors.secondary }}>
          Loading question…
        </span>
      </div>
    );
  }

  return (
    <div style={activityContainerStyle(isFeed)}>
      <div style={titleStyle(isFeed)}>
        {isReverse ? "How do you say this?" : "What does this mean?"}
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 0,
        }}
      >
        <ParrotBubble variant={variant} parrotImageSrc={parrotSitting}>
          {data.question}
        </ParrotBubble>
      </div>

      <div style={buttonsGridStyle}>
        {options.map((option, index) => (
          <div key={`${option.choice}-${index}`} style={gridCellStyle}>
            <ActivityButton
              ref={(r) => {
                buttonRefs.current[index] = r;
              }}
              text={option.choice}
              variant={variant}
              disabled={clickedIndex !== null && clickedIndex !== index}
              state={buttonStates[index] ?? "default"}
              correctAnimationHoldMs={isFeed ? undefined : null}
              onPress={() => handleClick(option.correct, index)}
            />
          </div>
        ))}
      </div>

      {!isFeed && (
        <CorrectDrawer
          visible={showCorrectDrawer}
          // Reverse MCQ has an English prompt + Spanish options, so question and
          // answer are language-swapped relative to forward; keep the drawer's
          // spanish/english pair correct for both.
          spanishWord={isReverse ? correctChoice : data.question}
          englishWord={isReverse ? data.question : correctChoice}
          onContinue={() => {
            setShowCorrectDrawer(false);
            setClickedIndex(null);
            onFinish(false);
          }}
        />
      )}
    </div>
  );
}
